import { useEffect, useRef, useState } from 'react';
import { gameSounds } from '../lib/gameSounds';
import { Watermark } from './Watermark';

/**
 * Pass 28an — HypeFish fullscreen overlay (variant=fullscreen).
 *
 * A 1920×1080 transparent canvas that, instead of the octagonal popup,
 * stages each cast as a full-stage scene:
 *
 *   1. Water rises from the bottom of the screen when a cast begins
 *      (idle = no water, hidden background).
 *   2. The rod / line swings in from the top-left and drops a bobber
 *      onto the water surface.
 *   3. Wait phase: bobber gently bobs with rippling water.
 *   4. Bite phase: bobber yanks underwater + big splash.
 *   5. Reveal phase: the caught item rises from below the water,
 *      breaches the surface, and rises into a hero card with rarity
 *      glow + viewer name + flavor.
 *
 * Water recedes between casts when the engine clears `current`.
 */

type HypeFishRarity = 'uncommon' | 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';
type HypeFishPhase = 'idle' | 'casting' | 'waiting' | 'biting' | 'revealing';
type HypeFishWeather = 'default' | 'night' | 'storm' | 'tropical';

interface HypeFishItem {
  id: string;
  name: string;
  rarity: HypeFishRarity;
  emoji: string;
  flavor: string;
  imageUrl?: string | null;
}

interface HypeFishCurrentCast {
  viewer: string;
  item: HypeFishItem;
  gems: number;
  phase: HypeFishPhase;
  phaseEndsAt: number;
  waitDurationMs: number;
}

interface HypeFishState {
  current?: HypeFishCurrentCast | null;
}

interface GameStateEvent {
  gameType: string;
  state: unknown;
  config: unknown;
  isActive: boolean;
}

interface HypefishSnapshot {
  state: HypeFishState | null;
  config: unknown;
  isActive: boolean;
}

const RARITY: Record<
  HypeFishRarity,
  { label: string; frame: string; glow: string; gradient: string; waterTint: string }
> = {
  common: {
    label: 'Common',
    frame: '#94a3b8',
    glow: 'rgba(148,163,184,0.65)',
    gradient: 'linear-gradient(160deg, #475569 0%, #1e293b 100%)',
    waterTint: 'rgba(94,128,184,0.55)',
  },
  uncommon: {
    label: 'Uncommon',
    frame: '#34d399',
    glow: 'rgba(52,211,153,0.7)',
    gradient: 'linear-gradient(160deg, #047857 0%, #064e3b 100%)',
    waterTint: 'rgba(52,211,153,0.5)',
  },
  rare: {
    label: 'Rare',
    frame: '#60a5fa',
    glow: 'rgba(96,165,250,0.75)',
    gradient: 'linear-gradient(160deg, #1d4ed8 0%, #0c2e6e 100%)',
    waterTint: 'rgba(56,189,248,0.55)',
  },
  epic: {
    label: 'Epic',
    frame: '#c084fc',
    glow: 'rgba(192,132,252,0.8)',
    gradient: 'linear-gradient(160deg, #7c3aed 0%, #3b1078 100%)',
    waterTint: 'rgba(167,139,250,0.55)',
  },
  legendary: {
    label: 'Legendary',
    frame: '#fbbf24',
    glow: 'rgba(251,191,36,0.9)',
    gradient: 'linear-gradient(160deg, #f59e0b 0%, #b45309 60%, #7c2d12 100%)',
    waterTint: 'rgba(251,191,36,0.5)',
  },
  mythic: {
    label: 'Mythic',
    frame: '#fb7185',
    glow: 'rgba(251,113,133,1)',
    gradient:
      'linear-gradient(135deg, #f97316 0%, #ec4899 25%, #a855f7 55%, #06b6d4 85%, #fde047 100%)',
    waterTint: 'rgba(251,113,133,0.55)',
  },
};

const WEATHER: Record<HypeFishWeather, { water: string; surface: string; sky: string }> = {
  default: {
    water:
      'linear-gradient(180deg, rgba(14,116,165,0.45) 0%, rgba(8,47,73,0.85) 60%, rgba(2,21,35,0.95) 100%)',
    surface: '#38bdf8',
    sky: 'transparent',
  },
  night: {
    water:
      'linear-gradient(180deg, rgba(30,27,75,0.55) 0%, rgba(15,23,42,0.85) 60%, rgba(2,6,23,0.95) 100%)',
    surface: '#a5b4fc',
    sky: 'transparent',
  },
  storm: {
    water:
      'linear-gradient(180deg, rgba(71,85,105,0.55) 0%, rgba(15,23,42,0.85) 60%, rgba(0,0,0,0.95) 100%)',
    surface: '#94a3b8',
    sky: 'transparent',
  },
  tropical: {
    water:
      'linear-gradient(180deg, rgba(251,146,60,0.45) 0%, rgba(124,45,18,0.8) 60%, rgba(67,20,7,0.95) 100%)',
    surface: '#fdba74',
    sky: 'transparent',
  },
};

function weatherFromConfig(config: unknown): HypeFishWeather {
  const c = config as { weatherMode?: unknown; custom?: { weatherMode?: unknown } } | null;
  const raw =
    typeof c?.weatherMode === 'string' ? c.weatherMode : (c?.custom?.weatherMode ?? 'default');
  return raw === 'night' || raw === 'storm' || raw === 'tropical' ? raw : 'default';
}

const ANIMATIONS = `
@keyframes hff-waterRise { from { height: 0 } to { height: var(--water-h, 19vh) } }
@keyframes hff-waterFall { from { height: var(--water-h, 19vh) } to { height: 0 } }
@keyframes hff-waterRipple { 0%, 100% { transform: translateX(0) scaleX(1) } 50% { transform: translateX(-12px) scaleX(1.02) } }
@keyframes hff-surfaceWobble { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-3px) } }
@keyframes hff-lineDrop { 0% { transform: scaleY(0); opacity: 0 } 30% { transform: scaleY(0); opacity: 0 } 100% { transform: scaleY(1); opacity: 1 } }
@keyframes hff-bobberDrop { 0% { top: 0; opacity: 0 } 30% { top: 0; opacity: 0 } 90% { top: var(--bobber-top, 62vh); opacity: 1 } 100% { top: var(--bobber-top, 62vh); opacity: 1 } }
@keyframes hff-bobberBob { 0%, 100% { transform: translate(-50%, 0) rotate(-3deg) } 50% { transform: translate(-50%, -6px) rotate(3deg) } }
@keyframes hff-bobberYank { 0% { transform: translate(-50%, 0); opacity: 1 } 40% { transform: translate(-50%, 80px); opacity: 1 } 100% { transform: translate(-50%, 220px); opacity: 0 } }
@keyframes hff-splash { 0% { transform: translate(-50%, -50%) scale(0); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(4); opacity: 0 } }
@keyframes hff-bubble { 0% { transform: translateY(0) scale(0.6); opacity: 0 } 20% { opacity: 0.8 } 100% { transform: translateY(-30vh) scale(1.2); opacity: 0 } }
@keyframes hff-ripple { 0% { transform: translate(-50%, -50%) scale(0.3); opacity: 0.6 } 100% { transform: translate(-50%, -50%) scale(3); opacity: 0 } }
@keyframes hff-itemRise { 0% { transform: translate(-50%, 100%) scale(0.4); opacity: 0; filter: blur(8px) } 35% { transform: translate(-50%, 30%) scale(0.85); opacity: 0.6; filter: blur(2px) } 70% { transform: translate(-50%, -15%) scale(1.08); opacity: 1; filter: blur(0) } 100% { transform: translate(-50%, 0) scale(1); opacity: 1; filter: blur(0) } }
@keyframes hff-itemSurface { 0% { transform: translateY(140%) scale(0.6); opacity: 0; filter: blur(6px) } 45% { transform: translateY(40%) scale(0.92); opacity: 0.85; filter: blur(1px) } 75% { transform: translateY(-8%) scale(1.04); opacity: 1; filter: blur(0) } 100% { transform: translateY(0) scale(1); opacity: 1; filter: blur(0) } }
@keyframes hff-cardGlow { 0%, 100% { box-shadow: 0 0 36px var(--rarity-glow), 0 0 12px var(--rarity-glow) } 50% { box-shadow: 0 0 72px var(--rarity-glow), 0 0 22px var(--rarity-glow) } }
@keyframes hff-mythicPrism { from { background-position: 0% 50% } to { background-position: 200% 50% } }
@keyframes hff-sparkle { 0%, 100% { opacity: 0; transform: scale(0.6) } 40% { opacity: 1; transform: scale(1.4) } }
@keyframes hff-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes hff-fadeOut { from { opacity: 1 } to { opacity: 0 } }
`;

const WATER_RISE_MS = 900;
const WATER_FALL_MS = 700;
// Where the bobber sits relative to the bottom of the viewport — should
// land just at the water surface line.
// Pass 28an.2 — water height halved (was 38vh). Bobber lands right at
// the new surface (100 - water_h = 81vh from top).
const WATER_H_VH = 19;
const BOBBER_TOP_VH = 100 - WATER_H_VH;
// 2026-06-21 — the FishScene sits inside a 1920×1080 stage that's
// `transform: scale(...)` to fit the viewport, but `vh` units inside
// resolve against the actual browser viewport, not the stage. That
// throws the reveal card into the middle of the screen when the
// OBS source is anything other than 1080 tall. These px constants
// pin layout to the stage canvas so the card always lands at the
// designed spot regardless of the source's pixel dimensions.
const STAGE_H_PX = 1080;
const WATER_H_PX = Math.round((WATER_H_VH / 100) * STAGE_H_PX);

export function HypefishFullscreenWidget({
  gameStateEvent,
  // Pass 28dl — declared in the prop TYPE but never destructured,
  // so paid streamers were getting the watermark anyway.
  streamerTier = 'free',
}: {
  gameStateEvent: GameStateEvent | null;
  streamerTier?: 'free' | 'paid';
}) {
  const [snapshot, setSnapshot] = useState<HypefishSnapshot | null>(null);
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    const tokenMatch = window.location.search.match(/token=([^&]+)/);
    if (!tokenMatch) return;
    bootstrappedRef.current = true;
    const ac = new AbortController();
    fetch(`/api/hypefish/snapshot?token=${tokenMatch[1]}`, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((json: HypefishSnapshot | null) => {
        if (json) setSnapshot(json);
      })
      .catch(() => undefined);
    return () => ac.abort();
  }, []);

  useEffect(() => {
    if (!gameStateEvent || gameStateEvent.gameType !== 'hypefish') return;
    setSnapshot({
      state: gameStateEvent.state as HypeFishState | null,
      config: gameStateEvent.config,
      isActive: gameStateEvent.isActive,
    });
  }, [gameStateEvent]);

  // Sound transitions — fire on phase edges so we don't double-play.
  const prevPhaseRef = useRef<HypeFishPhase | 'gone'>('gone');
  useEffect(() => {
    const cur = snapshot?.state?.current ?? null;
    const phase: HypeFishPhase | 'gone' = cur?.phase ?? 'gone';
    if (phase !== prevPhaseRef.current) {
      if (phase === 'casting') void gameSounds.play('phase_change');
      else if (phase === 'biting') void gameSounds.play('correct');
      else if (phase === 'revealing') {
        const r = cur?.item.rarity;
        if (r === 'mythic') void gameSounds.play('celebration');
        else if (r === 'legendary') void gameSounds.play('winner');
        else void gameSounds.play('reveal');
      }
      prevPhaseRef.current = phase;
    }
  }, [snapshot?.state?.current]);

  const isActive = snapshot?.isActive ?? false;
  const current = isActive ? (snapshot?.state?.current ?? null) : null;

  // Lingering scene: when the engine clears `current`, keep painting
  // the previous reveal for ~1.2s while the water recedes so the
  // viewer's catch doesn't pop off-screen instantly.
  const [exiting, setExiting] = useState<HypeFishCurrentCast | null>(null);
  const exitTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (current) {
      if (exitTimerRef.current != null) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      setExiting(null);
      return;
    }
    setExiting((prev) => prev ?? snapshot?.state?.current ?? null);
    exitTimerRef.current = window.setTimeout(() => {
      setExiting(null);
      exitTimerRef.current = null;
    }, WATER_FALL_MS + 200);
    return () => {
      if (exitTimerRef.current != null) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const rendered = current ?? exiting;
  const sceneState: 'enter' | 'exit' = current ? 'enter' : 'exit';
  const weather = weatherFromConfig(snapshot?.config);

  return (
    <>
      <style>{ANIMATIONS}</style>
      {rendered && <FishScene cast={rendered} weather={weather} sceneState={sceneState} />}
      {streamerTier === 'free' && <Watermark />}
    </>
  );
}

function FishScene({
  cast,
  weather,
  sceneState,
}: {
  cast: HypeFishCurrentCast;
  weather: HypeFishWeather;
  sceneState: 'enter' | 'exit';
}) {
  const rarity = RARITY[cast.item.rarity] ?? RARITY.common;
  const palette = WEATHER[weather];
  const isMythic = cast.item.rarity === 'mythic';
  const isRevealing = cast.phase === 'revealing';
  const isBiting = cast.phase === 'biting';

  return (
    <div
      style={
        {
          position: 'fixed',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          fontFamily: '"Inter", system-ui, sans-serif',
          color: '#f1f5f9',
          '--rarity-glow': rarity.glow,
          '--water-h': `${WATER_H_VH}vh`,
          '--bobber-top': `${BOBBER_TOP_VH}vh`,
        } as React.CSSProperties
      }
    >
      {/* Water — rises from the bottom over WATER_RISE_MS on enter, falls back on exit. */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: `${WATER_H_VH}vh`,
          background: palette.water,
          animation: `${sceneState === 'enter' ? 'hff-waterRise' : 'hff-waterFall'} ${
            sceneState === 'enter' ? WATER_RISE_MS : WATER_FALL_MS
          }ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
          boxShadow: `inset 0 8px 18px rgba(0,0,0,0.45), inset 0 -8px 30px rgba(0,0,0,0.6)`,
        }}
      />

      {/* Animated water surface band — sits just under the water top edge. */}
      <div
        style={{
          position: 'absolute',
          left: '-6vw',
          right: '-6vw',
          bottom: `${WATER_H_VH}vh`,
          height: 6,
          background: `linear-gradient(90deg, transparent, ${palette.surface}, transparent)`,
          opacity: 0.7,
          filter: 'blur(2px)',
          animation: `hff-waterRipple 3.4s ease-in-out infinite, ${
            sceneState === 'enter' ? 'hff-fade' : 'hff-fadeOut'
          } ${WATER_RISE_MS}ms ease-out forwards`,
          animationDelay: '0s, 0s',
        }}
      />

      {/* Rarity-tinted underwater glow when revealing — pulls the eye
          to where the item will breach. Scaled down with the water. */}
      {isRevealing && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '2vh',
            width: '40vw',
            height: `${WATER_H_VH - 2}vh`,
            background: `radial-gradient(ellipse at 50% 100%, ${rarity.waterTint} 0%, transparent 70%)`,
            transform: 'translateX(-50%)',
            filter: 'blur(8px)',
            animation: `hff-fade 600ms ease-out`,
          }}
        />
      )}

      {/* Line + bobber. Hide during reveal so the item has the stage.
          Pass 28an.1 — rod removed; line drops straight down from the
          top edge of the canvas so the focus stays on the bobber +
          water. */}
      {(cast.phase === 'casting' || cast.phase === 'waiting' || cast.phase === 'biting') && (
        <>
          {/* Fishing line — straight drop from the top edge to the bobber */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              width: 2,
              height: `${BOBBER_TOP_VH}vh`,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.85), rgba(255,255,255,0.4))',
              transformOrigin: 'top center',
              animation:
                cast.phase === 'casting' ? 'hff-lineDrop 700ms ease-out 0.1s both' : undefined,
              boxShadow: '0 0 2px rgba(255,255,255,0.6)',
            }}
          />
          {/* Bobber */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: `${BOBBER_TOP_VH}vh`,
              width: 56,
              height: 56,
              background: 'radial-gradient(circle at 30% 30%, #fef9c3, #ef4444 65%, #7f1d1d)',
              borderRadius: '50%',
              transform: 'translate(-50%, 0)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.6), inset 0 2px 4px rgba(255,255,255,0.5)',
              animation:
                cast.phase === 'casting'
                  ? 'hff-bobberDrop 700ms ease-out 0.25s both'
                  : cast.phase === 'biting'
                    ? 'hff-bobberYank 480ms ease-in forwards'
                    : 'hff-bobberBob 1.7s ease-in-out infinite',
            }}
          />
          {/* Concentric ripples when the bobber sits or yanks. */}
          {(cast.phase === 'waiting' || cast.phase === 'biting') && (
            <>
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: `calc(${BOBBER_TOP_VH}vh + 20px)`,
                  width: 200,
                  height: 60,
                  borderRadius: '50%',
                  border: `2px solid ${palette.surface}`,
                  opacity: 0,
                  transform: 'translate(-50%, -50%)',
                  animation: 'hff-ripple 2.4s ease-out infinite',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: `calc(${BOBBER_TOP_VH}vh + 20px)`,
                  width: 200,
                  height: 60,
                  borderRadius: '50%',
                  border: `2px solid ${palette.surface}`,
                  opacity: 0,
                  transform: 'translate(-50%, -50%)',
                  animation: 'hff-ripple 2.4s ease-out 1.2s infinite',
                }}
              />
            </>
          )}
          {/* Splash on bite. */}
          {isBiting && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: `calc(${BOBBER_TOP_VH}vh + 20px)`,
                width: 320,
                height: 320,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${palette.surface}aa 0%, ${palette.surface}00 70%)`,
                transform: 'translate(-50%, -50%) scale(0)',
                animation: 'hff-splash 700ms ease-out forwards',
              }}
            />
          )}
        </>
      )}

      {/* Bubbles rising in the water during waiting / biting / revealing. */}
      {(cast.phase === 'waiting' || cast.phase === 'biting' || cast.phase === 'revealing') && (
        <BubblesLayer surface={palette.surface} />
      )}

      {/* The fished item rises out of the water on reveal. */}
      {isRevealing && <RevealedItem cast={cast} rarity={rarity} isMythic={isMythic} />}
    </div>
  );
}

function BubblesLayer({ surface }: { surface: string }) {
  const bubbles = Array.from({ length: 12 }, (_, i) => i);
  return (
    <>
      {bubbles.map((i) => {
        const left = 20 + Math.random() * 60;
        const delay = Math.random() * 2.5;
        const size = 6 + Math.random() * 16;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${left}%`,
              bottom: '2vh',
              width: size,
              height: size,
              borderRadius: '50%',
              background: `${surface}80`,
              boxShadow: `0 0 8px ${surface}40`,
              opacity: 0,
              animation: `hff-bubble ${3 + Math.random() * 1.5}s ease-in ${delay}s infinite`,
            }}
          />
        );
      })}
    </>
  );
}

function RevealedItem({
  cast,
  rarity,
  isMythic,
}: {
  cast: HypeFishCurrentCast;
  rarity: (typeof RARITY)[HypeFishRarity];
  isMythic: boolean;
}) {
  // 2026-06-21 (centered+grown) — lift the card off the bottom so it
  // straddles the water surface line and bump width ~27% for a bit
  // more presence without going back to hero-size. Stage-pixel coords.
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        bottom: 120,
        transform: 'translateX(-50%)',
        animation: 'hff-itemSurface 1100ms cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
    >
      <div
        style={
          {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            width: 280,
            borderRadius: 16,
            padding: 14,
            background: isMythic
              ? rarity.gradient
              : `${rarity.gradient}, radial-gradient(120% 80% at 50% 0%, ${rarity.frame}33 0%, transparent 70%)`,
            backgroundSize: isMythic ? '200% 200%' : undefined,
            border: `2px solid ${rarity.frame}`,
            color: '#f1f5f9',
            animation: `hff-cardGlow 2.2s ease-in-out infinite${
              isMythic ? ', hff-mythicPrism 6s linear infinite' : ''
            }`,
          } as React.CSSProperties
        }
      >
        {/* Rarity ribbon */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <span
            style={{
              background: `${rarity.frame}26`,
              color: rarity.frame,
              border: `1px solid ${rarity.frame}80`,
              borderRadius: 999,
              padding: '3px 9px',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            🎣 {rarity.label}
          </span>
          {cast.gems > 0 && (
            <span
              style={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: 13,
                fontWeight: 700,
                color: '#fde047',
                textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                whiteSpace: 'nowrap',
              }}
            >
              +{cast.gems} 💎
            </span>
          )}
        </div>

        {/* Square artwork — smaller so the whole card stays compact. */}
        <div
          style={{
            position: 'relative',
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            aspectRatio: '1 / 1',
            background: 'rgba(0,0,0,0.35)',
            border: `2px solid ${rarity.frame}90`,
            borderRadius: 12,
            overflow: 'hidden',
            marginBottom: 8,
          }}
        >
          {cast.item.imageUrl ? (
            <img
              src={cast.item.imageUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ fontSize: 100 }}>{cast.item.emoji ?? '🎣'}</span>
          )}
          {(cast.item.rarity === 'legendary' || isMythic) && (
            <>
              {Array.from({ length: 5 }, (_, i) => i).map((i) => (
                <span
                  key={i}
                  style={{
                    position: 'absolute',
                    left: `${10 + Math.random() * 80}%`,
                    top: `${10 + Math.random() * 80}%`,
                    fontSize: 16,
                    color: rarity.frame,
                    textShadow: `0 0 6px ${rarity.glow}`,
                    animation: `hff-sparkle 1.6s ease-in-out ${Math.random() * 1.2}s infinite`,
                  }}
                >
                  ✦
                </span>
              ))}
            </>
          )}
        </div>

        {/* Name + viewer + flavor */}
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: 19,
              fontWeight: 800,
              lineHeight: 1.15,
              textShadow: '0 2px 6px rgba(0,0,0,0.7)',
            }}
          >
            {cast.item.emoji} {cast.item.name}
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              fontWeight: 600,
              color: '#cbd5e1',
            }}
          >
            Caught by <span style={{ color: '#fde047' }}>@{cast.viewer}</span>
          </div>
          {cast.item.flavor && (
            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color: '#94a3b8',
                fontStyle: 'italic',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              }}
            >
              {cast.item.flavor}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
